import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, ArrowRight, Target, Calendar, TrendingUp, Users, MessageSquare, Clock, Shield, Zap } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const GrowthStart = () => {
  const serviceBreakdown = [
    {
      category: "SEO Audit & Strategy",
      icon: Target,
      items: [
        "Complete technical SEO audit",
        "Keyword research and strategy",
        "Competitor analysis and positioning",
        "On-page optimization plan",
        "Local search visibility improvement"
      ]
    },
    {
      category: "Local SEO Optimization",
      icon: Calendar,
      items: [
        "Google My Business optimization",
        "Local citation building",
        "Review management setup",
        "Local keyword targeting",
        "Geographic search optimization"
      ]
    },
    {
      category: "Monthly Performance Reports",
      icon: TrendingUp,
      items: [
        "Search ranking tracking",
        "Website traffic analysis",
        "Lead generation metrics",
        "Conversion rate monitoring",
        "ROI performance insights"
      ]
    },
    {
      category: "Meta & Instagram Ads",
      icon: MessageSquare,
      items: [
        "Facebook advertising campaigns",
        "Instagram promotional content",
        "Audience targeting and segmentation",
        "Ad creative development",
        "Performance optimization and scaling"
      ]
    }
  ];

  const guarantees = [
    {
      icon: Shield,
      title: "90-Day Money-Back Guarantee",
      description: "If you don't see qualified leads within 90 days, we'll refund your investment completely."
    },
    {
      icon: Clock,
      title: "Setup Within 7 Days",
      description: "Your complete system will be live and generating leads within one week of signing up."
    },
    {
      icon: Zap,
      title: "First Lead Within 30 Days",
      description: "Most clients see their first qualified lead within the first 30 days of going live."
    }
  ];

  return (
    <>
      <EnhancedSEO 
        title="Growth Starter Package - Complete Client Acquisition System | VGP"
        description="Discover our comprehensive Growth Starter program that automates your entire client acquisition process. Get qualified leads booked directly into your calendar with our proven system."
        keywords="growth starter program, client acquisition system, automated lead generation, appointment booking, service business leads"
        url="/growth-start"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-background via-secondary/10 to-accent/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-lg px-6 py-2 bg-gradient-to-r from-primary/20 to-secondary/20 text-primary border-primary/30">
                Exclusive Growth Starter Program
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Complete Client Acquisition
                <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent"> System Breakdown</span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
                See exactly what's included in our Growth Starter program - the complete automated system that fills your calendar with qualified leads while you focus on delivering exceptional service.
              </p>

              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>7-Day Setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>90-Day Guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>No Long-Term Contracts</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Breakdown */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Everything You Get in Your Growth Starter System
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  A complete breakdown of every component that makes up your automated client acquisition machine in the Growth Starter package.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {serviceBreakdown.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Card key={index} className="border-border/50 hover:border-border transition-colors">
                      <CardHeader>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-xl text-foreground">{service.category}</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {service.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Investment & Guarantees */}
        <section className="py-16 bg-secondary/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Your Investment & Guarantees
                </h2>
                <p className="text-lg text-muted-foreground">
                  We're so confident in our system, we back it with iron-clad guarantees.
                </p>
              </div>

              {/* Investment */}
              <Card className="mb-12 border-2 border-accent/20">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-foreground mb-2">Growth Starter Program Investment</CardTitle>
                  <div className="text-4xl md:text-5xl font-bold text-accent mb-2">£599</div>
                  <CardDescription className="text-lg">
                    Per month for complete SEO strategy and meta advertising management
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-accent/10 rounded-lg p-6 text-center">
                    <p className="text-muted-foreground mb-4">
                      <strong>Everything Included:</strong> SEO audit, local optimization, monthly reports, and meta ads
                    </p>
                    <div className="text-2xl font-bold text-foreground">Complete Package</div>
                    <div className="text-lg text-accent mt-2">No setup fees • Month-to-month</div>
                  </div>
                </CardContent>
              </Card>

              {/* Guarantees */}
              <div className="grid md:grid-cols-3 gap-6">
                {guarantees.map((guarantee, index) => {
                  const IconComponent = guarantee.icon;
                  return (
                    <Card key={index} className="text-center border-border/50">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
                          <IconComponent className="w-8 h-8 text-primary" />
                        </div>
                        <CardTitle className="text-lg text-foreground">{guarantee.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription>{guarantee.description}</CardDescription>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Start Your Growth Starter Program?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Book your dedicated Growth Starter consultation to discuss your specific needs and get your automated lead generation system set up within 7 days.
              </p>
              
              <Button 
                asChild 
                variant="secondary" 
                size="xl"
                className="group transform hover:scale-105 transition-all duration-300"
              >
                <a href="/book-call">
                  Book Growth Starter Consultation
                  <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              
              <p className="text-white/80 text-sm mt-4">
                Dedicated Growth Starter consultation • Personalized strategy • 7-day setup guarantee
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GrowthStart;